import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Send } from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

const empty = {
  scout_id: "",
  stream: "amazon",
  source: "",
  amount_usd: "",
  external_ref: "",
  description: "",
  occurred_at: "",
};

export default function LogEarningsForm({ lang, knownSources, onLogged }) {
  const [scouts, setScouts] = useState([]);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/operator/scouts").then((r) => setScouts(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.scout_id || !form.source.trim() || !form.amount_usd) {
      toast.error(t(lang, "op_log_err"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        scout_id: form.scout_id,
        stream: form.stream,
        source: form.source.trim(),
        amount_usd: Number(form.amount_usd),
        description: form.description,
        external_ref: form.external_ref,
      };
      if (form.occurred_at) {
        payload.occurred_at = new Date(form.occurred_at).toISOString();
      }
      const { data } = await api.post("/operator/earnings", payload);
      toast.success(t(lang, "op_log_ok"), { description: `${data.source} · $${data.amount_usd}` });
      setForm(empty);
      onLogged && onLogged(data);
    } catch (err) {
      toast.error(t(lang, "op_log_err"));
    } finally {
      setBusy(false);
    }
  };

  const sourcesForStream = (knownSources && knownSources[form.stream]) || [];

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-2"
      data-testid="log-earnings-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl grid place-items-center border border-[#d4a843]/40 bg-[#d4a843]/10">
          <Package className="w-5 h-5 text-[#f3c669]" />
        </div>
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "op_log_earnings")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-md">
            {t(lang, "op_log_earnings_sub")}
          </p>
        </div>
      </div>

      <form
        onSubmit={submit}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        data-testid="log-earnings-form"
      >
        <Field label={t(lang, "op_pick_scout")}>
          <select
            className="eigoo-input"
            value={form.scout_id}
            onChange={set("scout_id")}
            data-testid="log-scout-select"
            required
          >
            <option value="">{t(lang, "op_pick_scout_ph")}</option>
            {scouts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} {s.referral_code ? `· ${s.referral_code}` : ""}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t(lang, "op_pick_stream")}>
          <select
            className="eigoo-input"
            value={form.stream}
            onChange={(e) => setForm((f) => ({ ...f, stream: e.target.value, source: "" }))}
            data-testid="log-stream-select"
          >
            <option value="amazon">{t(lang, "stream_amazon")}</option>
            <option value="marketplace">{t(lang, "stream_marketplace")}</option>
          </select>
        </Field>

        <Field label={t(lang, "op_pick_source")}>
          <input
            list="source-suggestions"
            className="eigoo-input"
            value={form.source}
            onChange={set("source")}
            placeholder={t(lang, "op_pick_source_ph")}
            data-testid="log-source-input"
            required
          />
          <datalist id="source-suggestions">
            {sourcesForStream.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </Field>

        <Field label={t(lang, "op_amount_usd")}>
          <input
            type="number"
            step="0.01"
            min="0.01"
            className="eigoo-input"
            value={form.amount_usd}
            onChange={set("amount_usd")}
            placeholder="12.50"
            data-testid="log-amount-input"
            required
          />
        </Field>

        <Field label={t(lang, "op_occurred_at")}>
          <input
            type="date"
            className="eigoo-input"
            value={form.occurred_at}
            onChange={set("occurred_at")}
            data-testid="log-occurred-input"
          />
        </Field>

        <Field label={t(lang, "op_external_ref")}>
          <input
            className="eigoo-input"
            value={form.external_ref}
            onChange={set("external_ref")}
            placeholder={t(lang, "op_external_ref_ph")}
            data-testid="log-ref-input"
          />
        </Field>

        <div className="sm:col-span-2">
          <Field label={t(lang, "op_desc")}>
            <textarea
              rows={2}
              className="eigoo-input resize-none"
              value={form.description}
              onChange={set("description")}
              placeholder={t(lang, "op_desc_ph")}
              data-testid="log-desc-input"
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="gold-btn inline-flex items-center gap-2"
            data-testid="log-earnings-submit"
          >
            <Send className="w-4 h-4" />
            {busy ? t(lang, "submitting") : t(lang, "op_log_submit")}
          </button>
        </div>
      </form>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
